import React, { useEffect, useRef } from 'react';
import { Language, t } from '../data/translations';
import triflePng from '../assets/custom/Trifle.png';

interface TriffleUnlockOverlayProps {
  language: Language;
  onAccept: () => void;
}

// Particle system adapted from brokenSecretEffect
class CardParticle {
    x: number; y: number; vx: number; vy: number;
    rot: number; rotV: number;
    w: number; h: number; color: string;
    life: number; maxLife: number;
    delay: number;
    trail: { x: number, y: number, alpha: number }[];

    constructor(startX: number, startY: number, delay: number) {
        this.x = startX; this.y = startY;
        const speed = 400 + Math.random() * 800; // Fast explosion
        const angle = Math.random() * Math.PI * 2; // Full radial
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.rot = Math.random() * Math.PI * 2;
        this.rotV = (Math.random() - 0.5) * 20;
        this.w = 15 + Math.random() * 20;
        this.h = this.w * 1.5;
        const colorPicks = ['#00ffff', '#00d2ff', '#e0b0ff', '#ff00ff', '#fdfc47', '#ff4b2b'];
        this.color = colorPicks[Math.floor(Math.random() * colorPicks.length)];
        this.maxLife = 1.5 + Math.random() * 1.0;
        this.life = 0;
        this.delay = delay;
        this.trail = [];
    }
    update(dt: number) {
        if (this.delay > 0) { this.delay -= dt; return; }
        this.life += dt;
        this.vy += 300 * dt; // gravity
        this.vx *= 0.95; // air friction
        this.vy *= 0.95;
        this.x += this.vx * dt;
        this.y += this.vy * dt;
        this.rot += this.rotV * dt;
        this.trail.push({ x: this.x, y: this.y, alpha: 1.0 });
        if (this.trail.length > 8) this.trail.shift();
        for (let i = 0; i < this.trail.length; i++) this.trail[i].alpha *= 0.8;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        const alpha = Math.max(0, 1 - (this.life / this.maxLife));
        ctx.globalAlpha = alpha;

        // Trail
        if (this.trail.length > 1) {
            ctx.beginPath();
            ctx.moveTo(this.trail[0].x, this.trail[0].y);
            for (let i = 1; i < this.trail.length; i++) ctx.lineTo(this.trail[i].x, this.trail[i].y);
            ctx.strokeStyle = this.color;
            ctx.lineWidth = 4 * alpha;
            ctx.lineCap = 'round';
            ctx.stroke();
        }

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.shadowBlur = 15;
        ctx.shadowColor = this.color;
        ctx.fillStyle = this.color;

        ctx.fillRect(-this.w / 2, -this.h / 2, this.w, this.h);
        ctx.fillStyle = '#ffffff'; // inner bright core
        ctx.fillRect(-this.w / 2 + 2, -this.h / 2 + 2, this.w - 4, this.h - 4);
        ctx.restore();
    }
}

const TriffleUnlockOverlay: React.FC<TriffleUnlockOverlayProps> = ({ language, onAccept }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let lastTime = performance.now();
        
        const resizeCanvas = () => {
             canvas.width = window.innerWidth;
             canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        let cx = window.innerWidth / 2;
        let cy = window.innerHeight / 2;

        const particles: CardParticle[] = [];
        // Spawn 80 cards bursting from the center
        for (let i = 0; i < 80; i++) {
            particles.push(new CardParticle(cx, cy, i * 0.005));
        }

        const render = (time: number) => {
            const dt = Math.min((time - lastTime) / 1000, 0.1);
            lastTime = time;

            cx = window.innerWidth / 2;
            cy = window.innerHeight / 2;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'screen';
            
            for (const p of particles) {
                p.update(dt);
                p.draw(ctx);
            }

            animationFrameId = requestAnimationFrame(render);
        };
        animationFrameId = requestAnimationFrame(render);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    const cssStyles = `
        @keyframes rgbGlow {
            0% { border-color: #ff0000; box-shadow: 0 0 20px #ff0000, inset 0 0 20px #ff0000; }
            16% { border-color: #ff00ff; box-shadow: 0 0 20px #ff00ff, inset 0 0 20px #ff00ff; }
            33% { border-color: #0000ff; box-shadow: 0 0 20px #0000ff, inset 0 0 20px #0000ff; }
            50% { border-color: #00ffff; box-shadow: 0 0 20px #00ffff, inset 0 0 20px #00ffff; }
            66% { border-color: #00ff00; box-shadow: 0 0 20px #00ff00, inset 0 0 20px #00ff00; }
            83% { border-color: #ffff00; box-shadow: 0 0 20px #ffff00, inset 0 0 20px #ffff00; }
            100% { border-color: #ff0000; box-shadow: 0 0 20px #ff0000, inset 0 0 20px #ff0000; }
        }
        @keyframes floatAir {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
        }
        @keyframes popIn {
            0% { transform: scale(0.5); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }
    `;

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm overflow-hidden overflow-y-auto">
            <style>{cssStyles}</style>
            
            <canvas 
                ref={canvasRef} 
                className="absolute inset-0 pointer-events-none"
            />

            <div className="relative z-10 flex flex-col items-center justify-center animate-[popIn_0.8s_cubic-bezier(0.175,0.885,0.32,1.275)_forwards]">
                <div className="animate-[floatAir_4s_ease-in-out_infinite]" style={{ animationDuration: '4s' }}>
                    <div className="rounded-xl overflow-hidden border-[6px] bg-black" style={{ animation: 'rgbGlow 3s linear infinite' }}>
                        <img 
                            src={triflePng} 
                            alt="Trifle Hero" 
                            className="w-64 sm:w-80 object-cover"
                        />
                    </div>
                </div>

                <div className="mt-8 text-center animate-[popIn_1s_ease-out_forwards]" style={{ animationDelay: '0.8s', opacity: 0 }}>
                    <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-500 mb-6 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] uppercase tracking-wider">
                        New Hero Unlocked!
                    </h2>
                    
                    <button
                        onClick={onAccept}
                        className="px-10 py-4 text-xl font-bold text-white rounded-xl shadow-lg transition-all transform hover:scale-105 active:scale-95 bg-gradient-to-r from-green-600 to-emerald-600 border-2 border-emerald-400 hover:shadow-[0_0_20px_rgba(52,211,153,0.6)]"
                    >
                        {t('ui.accept', language)}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TriffleUnlockOverlay;
